import { useLayoutEffect, useRef } from "react";
import type { CSSProperties } from "react";

/**
 * One line of text that shrinks its font size, never below `min`, until it fits the
 * width of its container. Used for KPI tile values such as "£15,291.94", which at a
 * fixed size can overflow a narrow tile. The line box keeps the preferred size and
 * the text sits on its baseline, so shrunk values still line up with their
 * neighbours. Re-fits whenever the container is resized.
 */
export function FitText({ children, size, min = 14, style }: {
  children: string;
  /** Preferred font size in px, used whenever the text fits. */
  size: number;
  /** Smallest font size in px; below this the text overflows rather than becoming unreadable. */
  min?: number;
  style?: CSSProperties;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const text = textRef.current;
    if (!box || !text) return;

    let lastWidth = -1;
    const fit = () => {
      const available = box.clientWidth;
      if (available === lastWidth) return;   // height-only change (from our own re-fit): nothing to do
      lastWidth = available;
      text.style.fontSize = `${size}px`;
      const natural = text.getBoundingClientRect().width;
      if (natural > available && natural > 0) {
        text.style.fontSize = `${Math.max(min, Math.floor((size * available) / natural))}px`;
      }
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [children, size, min]);

  return (
    <div ref={boxRef} style={{ ...style, fontSize: size, minWidth: 0 }}>
      <span ref={textRef} style={{ display: "inline-block", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
        {children}
      </span>
    </div>
  );
}
